/**
 * GA4 event name constants and parameter types.
 *
 * Naming convention: area.action_verb in snake_case
 * e.g. recipe.search, meal_plan.assign_recipe
 */

// ─── Auth ────────────────────────────────────────────────────────────
export const AUTH_EVENTS = {
  LOGIN_SUBMIT: 'auth.login_submit',
  LOGIN_SUCCESS: 'auth.login_success',
  LOGIN_FAIL: 'auth.login_fail',
  SIGNUP_SUBMIT: 'auth.signup_submit',
  SIGNUP_SUCCESS: 'auth.signup_success',
  SIGNUP_FAIL: 'auth.signup_fail',
  GOOGLE_CLICK: 'auth.google_click',
  LOGOUT: 'auth.logout',
  RESEND_VERIFICATION: 'auth.resend_verification',
} as const;

// ─── Dashboard ───────────────────────────────────────────────────────
export const DASHBOARD_EVENTS = {
  CTA_CLICK: 'dashboard.cta_click',
} as const;

// ─── Meal Plan ───────────────────────────────────────────────────────
export const MEAL_PLAN_EVENTS = {
  WEEK_NAV: 'meal_plan.week_nav',
  VIEW_MODE: 'meal_plan.view_mode',
  ASSIGN_RECIPE: 'meal_plan.assign_recipe',
  QUICK_PLAN: 'meal_plan.quick_plan',
  OVERRIDE: 'meal_plan.override',
  RECLASSIFY: 'meal_plan.reclassify',
  GEN_GROCERY: 'meal_plan.gen_grocery',
  LOG_MEAL: 'meal_plan.log_meal',
  RATE_MEAL: 'meal_plan.rate_meal',
  SUGGESTION_PICK: 'meal_plan.suggestion_pick',
} as const;

// ─── Recipes ─────────────────────────────────────────────────────────
export const RECIPE_EVENTS = {
  SEARCH: 'recipe.search',
  FILTER: 'recipe.filter',
  SORT: 'recipe.sort',
  CARD_CLICK: 'recipe.card_click',
  PAGINATE: 'recipe.paginate',
  ARCHIVE: 'recipe.archive',
  SOURCE_CLICK: 'recipe.source_click',
} as const;

// ─── Recipe Import ───────────────────────────────────────────────────
export const RECIPE_IMPORT_EVENTS = {
  URL_SUBMIT: 'recipe_import.url_submit',
  MANUAL_START: 'recipe_import.manual_start',
  STRATEGY_SELECT: 'recipe_import.strategy_select',
  SAVE: 'recipe_import.save',
  DISCARD: 'recipe_import.discard',
} as const;

// ─── Calendar ────────────────────────────────────────────────────────
export const CALENDAR_EVENTS = {
  MONTH_NAV: 'calendar.month_nav',
  SYNC: 'calendar.sync',
  EVENT_CLICK: 'calendar.event_click',
  SYNC_DISMISS: 'calendar.sync_dismiss',
  VIEW_TOGGLE: 'calendar.view_toggle',
} as const;

// ─── Shopping ────────────────────────────────────────────────────────
export const SHOPPING_EVENTS = {
  PRINT: 'shopping.print',
  ADD_PANTRY: 'shopping.add_pantry',
} as const;

// ─── Settings ────────────────────────────────────────────────────────
export const SETTINGS_EVENTS = {
  THEME_TOGGLE: 'settings.theme_toggle',
  NAV_CARD: 'settings.nav_card',
} as const;

// ─── Family Settings ─────────────────────────────────────────────────
export const FAMILY_EVENTS = {
  ADD_MANAGED: 'family.add_managed',
  INVITE_MEMBER: 'family.invite_member',
  CHANGE_ROLE: 'family.change_role',
  REMOVE_MEMBER: 'family.remove_member',
  REVOKE_INVITE: 'family.revoke_invite',
} as const;

// ─── Calendar Settings ──────────────────────────────────────────────
export const CAL_SETTINGS_EVENTS = {
  CONNECT_GOOGLE: 'cal_settings.connect_google',
  DISCONNECT: 'cal_settings.disconnect',
  TOGGLE_CALENDAR: 'cal_settings.toggle_calendar',
  SAVE: 'cal_settings.save',
  ADD_ICAL: 'cal_settings.add_ical',
  REMOVE_ICAL: 'cal_settings.remove_ical',
  SYNC_ICAL: 'cal_settings.sync_ical',
} as const;

// ─── School Lunch ───────────────────────────────────────────────────
export const SCHOOL_LUNCH_EVENTS = {
  ADD_SUBSCRIPTION: 'school_lunch.add_subscription',
  REMOVE_SUBSCRIPTION: 'school_lunch.remove_subscription',
  SYNC: 'school_lunch.sync',
  VIEW_MENU: 'school_lunch.view_menu',
} as const;

// ─── Catalogue ───────────────────────────────────────────────────────
export const CATALOGUE_EVENTS = {
  TAB_SWITCH: 'catalogue.tab_switch',
} as const;

// ─── Reporting ───────────────────────────────────────────────────────
export const REPORTING_EVENTS = {
  PERIOD_CHANGE: 'reporting.period_change',
} as const;

// ─── Navigation ──────────────────────────────────────────────────────
export const NAV_EVENTS = {
  SIDEBAR_CLICK: 'nav.sidebar_click',
  MOBILE_CLICK: 'nav.mobile_click',
  SIDEBAR_TOGGLE: 'nav.sidebar_toggle',
} as const;

// ─── Invite ──────────────────────────────────────────────────────────
export const INVITE_EVENTS = {
  ACCEPT: 'invite.accept',
} as const;

// ─── Page Enrichment ─────────────────────────────────────────────────
export const PAGE_ENRICHMENT_EVENT = 'page.data_density' as const;

// ─── Parameter Types ─────────────────────────────────────────────────
export type EventParams = Record<string, string | number | boolean | undefined>;
